import { useGlobalAppStateStore } from '@unifyapps/carbon/no-code/stores/app/GlobalAppStateStore';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import type { EventTypeEnum } from '@unifyapps/defs/types/event';
import { useCallback } from 'react';
import { propagateEventsToWebview } from '../../../../../webview/SDKHandling';
import type { OnActionType } from '../../../../context/ActionsProvider/context';
import useInterfaceEventBus from '../../../../hooks/useInterfaceEventBus';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../stores/GlobalStateStore';
import { useGetActivePageByInstanceId } from '../../../../stores/InterfaceStore';
import { appendIntegrationPageEvents } from '../../../../utils/appendIntegrationPageEvents';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { handleIntegrationPageEvent } from './handleIntegrationPageEvent';
import propagateEventsToIframe from './targets/iframe';
import type { PageEvent } from './types';

export type PageEventPayload = {
  eventType: EventTypeEnum;
  eventPayload?: unknown;
  // In case of Delta-matrix we need this eventName to distinguish between different custom events without creating Page Events on UI.
  eventName?: string;
};

function propagateEvents(event: PageEvent) {
  propagateEventsToWebview(event);
  propagateEventsToIframe(event);
}

function useEmitPageEvent({ emitPageEvent }: { emitPageEvent?: EmitEventType }) {
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const getActivePageByInstanceId = useGetActivePageByInstanceId();
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const { interfaceEventBus } = useInterfaceEventBus();

  const onEmitPageEvent: OnActionType = useCallback(
    async ({ action, actionContext, instanceId }) => {
      const payload = action.payload as PageEventPayload;
      const { eventType, eventPayload: unComputedEventPayload, eventName: _eventName } = payload;
      const { value: eventPayload } = getNoCodeComputedData<{
        value: unknown;
      }>(
        {
          value: unComputedEventPayload,
        },
        actionContext,
      );

      // resolve events against the target instance's active page so a propagated action from a nested inline instance reads the correct event list
      const pageEvents = getActivePageByInstanceId(instanceId)?.properties.events;

      const supportedPageEvents = appendIntegrationPageEvents({ interfaceRecord, pageEvents });
      const eventName =
        _eventName ??
        (supportedPageEvents.find((event) => event.eventType === eventType)
          ?.label as unknown as string);

      const event = {
        label: eventName,
        eventType,
        eventPayload,
      };

      // try to handle this event as an event originating from any integration ex. Copilot, Ozonetel etc.
      handleIntegrationPageEvent({ event, interfaceEventBus });

      propagateEvents(event);
      // prefer the per-instance emitter seeded by usePageInstanceInit (mirrors onOutputChange):
      // inline page instances (Module, TemplateComponent) don't re-wrap InterfaceActionProvider,
      // so the closure `emitPageEvent` is the outer tree's emitter. the store-resolved emitter
      // lets an embedded action target the correct host via its instanceId. fall back to the
      // closure for top-level trees where the emitter comes from InterfaceActionProvider
      // (e.g. main page runner, CopilotChatInterface).
      const instanceEmitter = getGlobalStateStoreState(instanceId).emitPageEvent;
      const emitter = instanceEmitter ?? emitPageEvent;
      await emitter?.(event);

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [
      getNoCodeComputedData,
      getActivePageByInstanceId,
      getGlobalStateStoreState,
      interfaceRecord,
      interfaceEventBus,
      emitPageEvent,
    ],
  );
  return { onEmitPageEvent };
}

export default useEmitPageEvent;
